import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { getLocalInfo, searchEvents, type LocalInfo } from "@/lib/api";

export const Route = createFileRoute("/destination/$iata")({
  component: DestinationPage,
  head: ({ params }) => ({
    meta: [
      { title: `Destino ${params.iata} — Sift Trip` },
      { name: "description", content: `Lo que nadie te cuenta sobre este destino. Tips locales, eventos y más.` },
    ],
  }),
});

function DestinationPage() {
  const { iata } = Route.useParams();
  const [city, setCity] = useState(iata);
  const [info, setInfo] = useState<LocalInfo | null>(null);
  const [events, setEvents] = useState<Array<{ name: string; date?: string; venue?: string; url?: string; is_free?: boolean }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const localData = await getLocalInfo(iata, iata);
        setInfo(localData);
      } catch { /* ignore */ }
      try {
        const evData = await searchEvents({ city: iata, max_results: 5 });
        setEvents(evData.events || []);
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetchData();
  }, [iata]);

  const infoCards = info ? [
    { icon: "🏘️", title: "Mejores barrios para quedarte", content: info.neighborhoods },
    { icon: "🍽️", title: "Qué comer sí o sí", content: info.food },
    { icon: "🚇", title: "Cómo moverse en la ciudad", content: info.transport },
    { icon: "💡", title: "Tip de Sift Trip", content: info.local_tip },
    { icon: "⚠️", title: "Cosas que debes saber", content: info.safety },
  ].filter((c) => c.content) : [];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <div className="relative py-20 px-4 text-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-accent/30 to-gold/10" />
        <div className="relative z-10 max-w-3xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-black">{city.toUpperCase()}</h1>
          <p className="text-gold text-lg mt-2">{iata}</p>
          <p className="text-xl text-muted-foreground mt-4">Lo que nadie te cuenta en TripAdvisor</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10">
        {loading ? (
          <div className="grid md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="rhea-card-static p-6">
                <div className="skeleton-pulse h-5 w-32 rounded mb-3" />
                <div className="skeleton-pulse h-4 w-full rounded mb-2" />
                <div className="skeleton-pulse h-4 w-3/4 rounded" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {infoCards.map((card, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="rhea-card-static p-6"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{card.icon}</span>
                    <h3 className="font-bold">{card.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{card.content}</p>
                </motion.div>
              ))}
            </div>

            {/* Events */}
            {events.length > 0 && (
              <div className="mb-10">
                <h2 className="text-2xl font-bold mb-4">🎉 Qué está pasando cuando llegas</h2>
                <div className="space-y-3">
                  {events.map((ev, i) => (
                    <div key={i} className="rhea-card-static p-4 flex items-center justify-between">
                      <div>
                        <div className="font-semibold">{ev.name}</div>
                        <div className="text-sm text-muted-foreground">{ev.date} {ev.venue ? `• ${ev.venue}` : ""}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {ev.is_free && <span className="text-xs badge-low px-2 py-0.5 rounded-full">Gratis</span>}
                        {ev.url && <a href={ev.url} target="_blank" rel="noopener noreferrer" className="text-sm text-gold hover:underline">Ver evento →</a>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="text-center">
              <Link to="/" className="btn-gold inline-block">
                Buscar vuelos a {city} ✈️
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
